'use client';

import { useEffect, useRef } from 'react';
import { getNotificationsStore } from '../store/notifications-store';
import { NotificationType } from '../types';

const POLL_INTERVAL_MS = 15_000;
const WARNING_MESSAGE =
  'Please wait – if an update is in progress, your work is safe and you will be able to continue once the connection is restored.';

const WARNING_DETAILS = (
  <>
    <strong>If the connection isn&apos;t restored</strong>
    <ul className="mt-1 list-disc pl-5">
      <li>Check your internet connection.</li>
      <li>Check that you&apos;re still connected to the VPN or the office WiFi.</li>
    </ul>
  </>
);

export default function BackendConnectivityMonitor() {
  const useNotificationStore = getNotificationsStore();
  const dismissNotification = useNotificationStore((state) => state.dismissNotification);
  const activeNotificationId = useRef<string | null>(null);

  useEffect(() => {
    let mounted = true;

    function isNotificationStillVisible() {
      if (!activeNotificationId.current) {
        return false;
      }
      const { notifications } = useNotificationStore.getState();
      return notifications.some((n) => n.id === activeNotificationId.current);
    }

    function showWarning() {
      if (isNotificationStillVisible()) {
        return;
      }
      activeNotificationId.current = null;
      const id = crypto.randomUUID();
      activeNotificationId.current = id;
      useNotificationStore.setState((state) => ({
        notifications: [
          ...state.notifications,
          {
            id,
            type: NotificationType.Warning,
            message: WARNING_MESSAGE,
            details: WARNING_DETAILS,
          },
        ],
      }));
    }

    function clearWarning() {
      if (!activeNotificationId.current) {
        return;
      }
      dismissNotification(activeNotificationId.current);
      activeNotificationId.current = null;
    }

    async function checkStatus() {
      try {
        const response = await fetch('/api/backend-status');
        if (!mounted) {
          return;
        }
        if (response.ok) {
          clearWarning();
        } else {
          showWarning();
        }
      } catch {
        if (!mounted) {
          return;
        }
        showWarning();
      }
    }

    const interval = setInterval(checkStatus, POLL_INTERVAL_MS);

    return () => {
      mounted = false;
      clearInterval(interval);
      clearWarning();
    };
  }, [dismissNotification, useNotificationStore]);

  return null;
}
